import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormField from './FormField';
import Toast from './Toast';

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

interface CustomizationFormProps {
  onSuccess?: () => void;
}

export default function CustomizationForm({ onSuccess }: CustomizationFormProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<SubmitState>('idle');
  const [name, setName] = useState('');
  const [orgname, setOrgname] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const isPhone = (val: string) => {
    const digits = val.replace(/\D/g, "");
    return digits.length >= 7 && digits.length <= 15;
  };
  const isEmail = (val: string) => /^([a-zA-Z0-9_.+-])+\@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/.test(val);

  const validators = {
    name: (val: string) => {
      if (!val.trim()) return t('scheme.form.errors.name');
      return null;
    },
    orgname: (val: string) => {
      if (!val.trim()) return t('scheme.form.errors.orgname');
      return null;
    },
    phone: (val: string) => {
      if (!val.trim()) return t('scheme.form.errors.phone');
      if (!isPhone(val)) return t('scheme.form.errors.phone');
      return null;
    },
    email: (val: string) => {
      if (!val.trim()) return t('scheme.form.errors.email');
      if (!isEmail(val)) return t('scheme.form.errors.email');
      return null;
    },
    message: (val: string) => {
      if (!val.trim()) return t('scheme.form.errors.message');
      return null;
    },
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    Object.entries(validators).forEach(([field, validator]) => {
      const value = { name, orgname, phone, email, message }[field as keyof typeof validators];
      const error = validator(value);
      if (error) {
        errors[field] = error;
      }
    });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const scrollToFirstError = () => {
    const firstErrorField = document.querySelector('.border-red-400, .border-red-500');
    if (firstErrorField) {
      firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (firstErrorField as HTMLElement).focus();
    }
  };

  const onSubmit = async () => {
    if (!validateForm()) {
      setToast({ message: t('scheme.form.errors.validation'), type: 'error' });
      // 延迟滚动以确保错误状态已更新
      setTimeout(scrollToFirstError, 100);
      return;
    }

    try {
      setState('submitting');
      const params = new URLSearchParams({
        name,
        phone,
        orgname,
        email,
        message,
        page: window.location.href,
      });
      const res = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
        body: params,
      });
      const data = await res.json().catch(() => ({}));
      if ((data as any).code === 1) {
        setState('success');
        onSuccess?.();
      } else {
        setState('error');
        setToast({ message: t('scheme.form.errors.network'), type: 'error' });
      }
    } catch {
      setState('error');
      setToast({ message: t('scheme.form.errors.network'), type: 'error' });
    }
  };

  if (state === 'success') {
    return (
      <div className="text-center pt-[50px] pb-[100px]">
        <div className="mb-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
            <span className="text-3xl text-green-500">✓</span>
          </div>
        </div>
        <p className="leading-[30px] text-[#666] text-xl font-semibold w-2xl mx-auto">{t('scheme.form.success')}</p>
      </div>
    );
  }

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div
        className="px-[5%] py-5 overflow-hidden"
        data-aos="flip-left"
        data-aos-easing="ease-out-cubic"
        data-aos-duration="700"
      >
        <div className="grid grid-cols-2 gap-x-[35px] gap-y-[10px] mb-[30px]">
          <FormField
            type="text"
            placeholder={t('scheme.form.name')}
            value={name}
            onChange={setName}
            error={fieldErrors.name}
            validate={validators.name}
            required
          />
          <FormField
            type="text"
            placeholder={t('scheme.form.orgname')}
            value={orgname}
            onChange={setOrgname}
            error={fieldErrors.orgname}
            validate={validators.orgname}
            required
          />
          <FormField
            type="text"
            placeholder={t('scheme.form.phone')}
            value={phone}
            onChange={setPhone}
            error={fieldErrors.phone}
            validate={validators.phone}
            required
          />
          <FormField
            type="text"
            placeholder={t('scheme.form.email')}
            value={email}
            onChange={setEmail}
            error={fieldErrors.email}
            validate={validators.email}
            required
          />
        </div>
        <FormField
          type="textarea"
          placeholder={t('scheme.form.message')}
          value={message}
          onChange={setMessage}
          error={fieldErrors.message}
          validate={validators.message}
          required
        />
        <button
          type="button"
          onClick={onSubmit}
          disabled={state === 'submitting'}
          className={`
            text-base text-white bg-[#0f5f95] w-[160px] text-center leading-[44px] rounded
            cursor-pointer mx-auto block transition-all duration-200
            hover:bg-[#0d4f7a] hover:shadow-md my-6
            disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#0f5f95]
            ${state === 'submitting' ? 'animate-pulse' : ''}
          `}
        >
          {state === 'submitting' ? t('scheme.form.submitting') : t('scheme.form.submit')}
        </button>
      </div>
    </>
  );
}

